import React, { useState, useMemo } from 'react';
import { useLocale } from '@platform/core';
import { MagicGridRow, MagicGridOptions, ShowField, SortState } from './types';

interface MagicGridTableProps {
  rows: MagicGridRow[];
  search: string;
  options?: MagicGridOptions;
  editable?: boolean;
  deletable?: boolean;
  duplicatable?: boolean;
  showFields: ShowField[];
  onShowFieldToggle: (key: string) => void;
  settingsMode: boolean;
  onUpdate: (id: string, data: Record<string, any>) => void;
  onDelete: (id: string) => void;
  onDuplicate: (row: MagicGridRow) => void;
}

export default function MagicGridTable({
  rows,
  search,
  options,
  editable,
  deletable,
  duplicatable,
  showFields,
  onShowFieldToggle,
  settingsMode,
  onUpdate,
  onDelete,
  onDuplicate,
}: MagicGridTableProps) {
  const { t } = useLocale();
  const [sort, setSort] = useState<SortState>({ key: '', desc: false });
  const [editingRow, setEditingRow] = useState<MagicGridRow | null>(null);
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;

  // Determine visible columns
  const hideCols = new Set([...(options?.hideCols || []), 'id']);
  const roCols = new Set(options?.roCols || []);

  const visibleFields = useMemo(() => {
    return showFields.filter(
      (f) => f.show && !hideCols.has(f.key)
    );
  }, [showFields, hideCols]);

  // Filter rows by search
  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter((row) =>
      Object.values(row).some(
        (v) => v != null && String(v).toLowerCase().includes(s)
      )
    );
  }, [rows, search]);

  // Sort rows
  const sortedRows = useMemo(() => {
    if (!sort.key) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const aVal = a[sort.key] ?? '';
      const bVal = b[sort.key] ?? '';
      const cmp =
        typeof aVal === 'number' && typeof bVal === 'number'
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal));
      return sort.desc ? -cmp : cmp;
    });
  }, [filteredRows, sort]);

  // Paginate
  const totalPages = Math.ceil(sortedRows.length / perPage);
  const pageRows = sortedRows.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const handleSort = (key: string) => {
    setSort((prev) =>
      prev.key === key ? { key, desc: !prev.desc } : { key, desc: false }
    );
  };

  const startEdit = (row: MagicGridRow) => {
    setEditingRow(row);
    setEditData({ ...row });
    setDeletingId(null);
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditData({});
  };

  const submitEdit = () => {
    if (!editingRow?.id) return;
    const { id: _id, ...updates } = editData;
    onUpdate(editingRow.id, updates);
    setEditingRow(null);
    setEditData({});
  };

  const startDelete = (id: string) => {
    setDeletingId(id);
    setEditingRow(null);
  };

  const confirmDelete = () => {
    if (deletingId) {
      onDelete(deletingId);
      setDeletingId(null);
    }
  };

  const renderCellValue = (value: any): string => {
    if (value == null) return '';
    if (typeof value === 'boolean') return value ? '✅' : '❌';
    if (typeof value === 'object') {
      // Firestore Timestamp
      if (value.seconds || value._seconds) {
        return new Date((value.seconds || value._seconds) * 1000).toLocaleDateString();
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  return (
    <div className="mg-table-container">
      {/* Column visibility toggles */}
      {settingsMode && (
        <div className="mg-field-toggles">
          <strong>{t('common.columns') || 'Columns'}:</strong>
          {showFields
            .filter((f) => !hideCols.has(f.key))
            .map((f) => (
              <button
                key={f.key}
                className={`mg-btn mg-btn-sm ${f.show ? 'mg-btn-primary' : 'mg-btn-outline'}`}
                onClick={() => onShowFieldToggle(f.key)}
              >
                {f.show ? '👁️' : '🚫'} {f.key}
              </button>
            ))}
        </div>
      )}

      <table className="mg-table">
        <thead>
          <tr>
            {visibleFields.map((f) => (
              <th
                key={f.key}
                onClick={() => handleSort(f.key)}
                className="mg-th-sortable"
              >
                {f.key}
                {sort.key === f.key && (sort.desc ? ' ▼' : ' ▲')}
              </th>
            ))}
            {(editable || deletable) && <th>{t('common.actions') || 'Actions'}</th>}
          </tr>
        </thead>
        <tbody>
          {pageRows.length === 0 && (
            <tr>
              <td
                colSpan={visibleFields.length + (editable || deletable ? 1 : 0)}
                className="mg-empty"
              >
                {t('common.noData') || 'No data'}
              </td>
            </tr>
          )}
          {pageRows.map((row) => {
            const isEditing =
              editingRow != null && editingRow.id === row.id;
            const isDeleting = deletingId === row.id;

            return (
              <tr
                key={row.id || JSON.stringify(row)}
                className={isEditing ? 'mg-row-editing' : ''}
              >
                {visibleFields.map((f) => (
                  <td key={f.key}>
                    {isEditing && !roCols.has(f.key) ? (
                      typeof editData[f.key] === 'boolean' ? (
                        <input
                          type="checkbox"
                          checked={editData[f.key]}
                          onChange={(e) =>
                            setEditData((d) => ({
                              ...d,
                              [f.key]: e.target.checked,
                            }))
                          }
                        />
                      ) : (
                        <input
                          className="mg-cell-input"
                          value={editData[f.key] ?? ''}
                          onChange={(e) =>
                            setEditData((d) => ({
                              ...d,
                              [f.key]: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') submitEdit();
                            if (e.key === 'Escape') cancelEdit();
                          }}
                        />
                      )
                    ) : (
                      <span
                        onClick={() => editable && startEdit(row)}
                        style={{ cursor: editable ? 'pointer' : 'default' }}
                      >
                        {renderCellValue(row[f.key])}
                      </span>
                    )}
                  </td>
                ))}
                {(editable || deletable) && (
                  <td className="mg-actions-cell">
                    {isEditing ? (
                      <>
                        <button
                          className="mg-btn mg-btn-sm mg-btn-primary"
                          onClick={submitEdit}
                        >
                          {t('common.update') || 'UPDATE'}
                        </button>
                        <button
                          className="mg-btn mg-btn-sm mg-btn-outline"
                          onClick={cancelEdit}
                        >
                          {t('common.cancel') || 'CANCEL'}
                        </button>
                      </>
                    ) : isDeleting ? (
                      <>
                        <button
                          className="mg-btn mg-btn-sm mg-btn-danger"
                          onClick={confirmDelete}
                        >
                          {t('common.delete') || 'DELETE'}
                        </button>
                        <button
                          className="mg-btn mg-btn-sm mg-btn-outline"
                          onClick={() => setDeletingId(null)}
                        >
                          {t('common.cancel') || 'CANCEL'}
                        </button>
                      </>
                    ) : (
                      <>
                        {editable && (
                          <button
                            className="mg-btn mg-btn-sm mg-btn-outline"
                            onClick={() => startEdit(row)}
                            title="Edit"
                          >
                            ✏️
                          </button>
                        )}
                        {duplicatable && (
                          <button
                            className="mg-btn mg-btn-sm mg-btn-outline"
                            onClick={() => onDuplicate(row)}
                            title="Duplicate"
                          >
                            📋
                          </button>
                        )}
                        {deletable && (
                          <button
                            className="mg-btn mg-btn-sm mg-btn-outline"
                            onClick={() => startDelete(row.id!)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        )}
                      </>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mg-pagination">
          <button
            className="mg-btn mg-btn-sm mg-btn-outline"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            ←
          </button>
          <span>
            {currentPage} / {totalPages}
          </span>
          <button
            className="mg-btn mg-btn-sm mg-btn-outline"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            →
          </button>
          <span className="mg-row-count">
            ({sortedRows.length} {t('common.rows') || 'rows'})
          </span>
        </div>
      )}
    </div>
  );
}
